import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, Loader2 } from 'lucide-react';
import SearchSelect from '../shared/SearchSelect';
import { InfoTip, LockTip } from '../shared/FormKit';
import { STAFF_GRADES } from '../../utils/staffGrades';
import { getLdSettings } from '../../utils/dateRange';
import { formatDate } from '../../utils/dateFormat';
import {
  RULE_META, RULE_ORDER, RULE_GROUPS, PROFILE_KEYS,
  normalizeRule, isRuleIncomplete, rulePayload, profileIsEmpty,
} from './audienceRuleMeta';

/**
 * The pop-up where one expected-attendee rule is put together: pick a rule
 * type, fill it in, watch who it matches, then add it to the list. Editing an
 * existing rule opens the same window on that rule.
 *
 * Props:
 *   rule            — the rule being edited, or null to add a new one
 *   usedTypes       — Set of types already on the list (single-use types are
 *                     greyed out in the type picker)
 *   excludeStaffIds — people struck off the list; the match count leaves
 *                     them out so it is the count the list will show
 *   trainerStaffIds — the session's trainers, dropped from the match count
 *                     as the save drops them
 *   lookupOptions   — { staff_department: [], staff_designation: [] }
 *   staffOptions / categoryOptions / qualTypeOptions / pastSessions
 *   fetchJSON       — the page's fetch helper
 *   onSave(rule)    — the finished rule
 *   onClose
 */

const fieldCls = 'px-2.5 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const pillCls = (on) => `px-3 py-1 text-xs rounded-lg border ${
  on ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`;

const toggleIn = (list, value, on) => (on
  ? [...list, value]
  : list.filter((v) => String(v) !== String(value)));

/** One scrollable tick list with a header, a tally and a Clear link. */
const CheckColumn = ({ title, options, selected, onChange, empty, maxH = 'max-h-64' }) => (
  <div className="min-w-0">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
        {title}
        {selected.length > 0 && (
          <span className="ml-1.5 normal-case tracking-normal font-normal text-slate-500">({selected.length})</span>
        )}
      </span>
      {selected.length > 0 && (
        <button type="button" onClick={() => onChange([])} className="text-xs text-blue-600 hover:underline">
          Clear
        </button>
      )}
    </div>
    <div className={`border rounded-lg bg-white ${maxH} overflow-y-auto ${
      selected.length > 0 ? 'border-blue-300' : 'border-slate-200'}`}
    >
      {options.map((o) => {
        const on = selected.some((v) => String(v) === String(o.value));
        return (
          <label
            key={o.value}
            className={`flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${
              on ? 'text-slate-900' : 'text-slate-700'}`}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={(e) => onChange(toggleIn(selected, o.value, e.target.checked))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded shrink-0"
            />
            <span className="truncate">{o.label}</span>
          </label>
        );
      })}
      {options.length === 0 && <p className="px-2.5 py-2 text-xs text-slate-500">{empty}</p>}
    </div>
  </div>
);

/**
 * Department | Grade | Designation side by side. Ticks across columns
 * combine (AND); ticks within a column are alternatives (OR); an empty column
 * is no condition. Used both as the whole of a profile rule and as the
 * "narrow down" block under a record-based rule.
 */
const ProfilePicker = ({ value, onChange, depOptions, desOptions }) => {
  const set = (key, list) => onChange({ ...value, [key]: list });
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <CheckColumn
        title="Department"
        options={depOptions}
        selected={value.departments}
        onChange={(l) => set('departments', l)}
        empty="No departments. Add them under Dropdown Lists."
      />
      <CheckColumn
        title="Grade"
        options={STAFF_GRADES.map((g) => ({ value: g, label: g }))}
        selected={value.grades}
        onChange={(l) => set('grades', l)}
        empty=""
      />
      <CheckColumn
        title="Designation"
        options={desOptions}
        selected={value.designations}
        onChange={(l) => set('designations', l)}
        empty="No designations. Add them under Dropdown Lists."
      />
    </div>
  );
};

const DateRange = ({ rule, onPatch, periodHint }) => (
  <div className="flex flex-wrap items-center gap-2">
    <input type="date" value={rule.from || ''} onChange={(e) => onPatch({ from: e.target.value })} className={fieldCls} aria-label="From" />
    <span className="text-slate-500 text-sm">to</span>
    <input type="date" value={rule.to || ''} onChange={(e) => onPatch({ to: e.target.value })} className={fieldCls} aria-label="To" />
    <span className="text-xs text-slate-500">{periodHint}</span>
  </div>
);

const AudienceRuleModal = ({
  rule = null, usedTypes = new Set(), excludeStaffIds = [], trainerStaffIds = [],
  lookupOptions = {}, staffOptions = [], categoryOptions = [], qualTypeOptions = [],
  pastSessions = [], fetchJSON, onSave, onClose,
}) => {
  const trainerKey = JSON.stringify(trainerStaffIds || []);
  const editing = !!rule;
  const [draft, setDraft] = useState(() => (rule ? normalizeRule(rule) : null));
  const [matches, setMatches] = useState({ loading: false, count: null, data: [], error: null });
  // The narrow-down block opens by itself when the rule being edited uses it.
  const [narrowOpen, setNarrowOpen] = useState(
    () => !!(rule && RULE_META[rule.type]?.narrowable && !profileIsEmpty(rule.where)));

  // Escape closes, as the form's own modal does.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const complete = draft ? !isRuleIncomplete(draft) : false;
  const payloadKey = complete ? JSON.stringify(rulePayload(draft)) : '';
  const excludeKey = JSON.stringify(excludeStaffIds);

  // Who this one rule matches, refreshed as it is filled in. Debounced so a
  // date box does not fire a request per keystroke.
  useEffect(() => {
    if (!payloadKey) {
      setMatches({ loading: false, count: null, data: [], error: null });
      return undefined;
    }
    let cancelled = false;
    setMatches((m) => ({ ...m, loading: true, error: null }));
    const timer = setTimeout(() => {
      fetchJSON('/api/ld/roster/preview', {
        method: 'POST',
        body: JSON.stringify({
          rules: { include: [JSON.parse(payloadKey)], exclude: { staffIds: JSON.parse(excludeKey) } },
          trainerStaffIds: JSON.parse(trainerKey),
        }),
      })
        .then((d) => { if (!cancelled) setMatches({ loading: false, count: d.count, data: d.data || [], error: null }); })
        .catch((e) => { if (!cancelled) setMatches({ loading: false, count: null, data: [], error: e.message }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [payloadKey, excludeKey, trainerKey, fetchJSON]);

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const toggle = (key, value, on) => setDraft((d) => ({ ...d, [key]: toggleIn(d[key] || [], value, on) }));

  const depOptions = useMemo(
    () => (lookupOptions.staff_department || []).map((v) => ({ value: v, label: v })), [lookupOptions]);
  const desOptions = useMemo(
    () => (lookupOptions.staff_designation || []).map((v) => ({ value: v, label: v })), [lookupOptions]);
  const staffName = (id) => staffOptions.find((s) => String(s.id) === String(id))?.name || `#${id}`;

  const meta = draft ? RULE_META[draft.type] : null;
  const hasAllActive = usedTypes.has('all_active');

  // ── Type picker (add mode, before a type is chosen) ──────────────────────
  const renderTypePicker = () => (
    <div className="space-y-4">
      {hasAllActive && (
        <p className="text-xs text-amber-600">
          Everyone is already included. A further rule cannot add anybody new.
        </p>
      )}
      {RULE_GROUPS.map((group) => (
        <div key={group.key}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{group.label}</p>
          <p className="text-xs text-slate-500 mb-2">{group.hint}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {RULE_ORDER.filter((t) => RULE_META[t].group === group.key).map((t) => {
              const m = RULE_META[t];
              const Icon = m.icon;
              const already = m.single && usedTypes.has(t);
              return (
                <LockTip key={t} text={already ? 'Already on the list — edit that rule instead' : null} block>
                  <button
                    type="button"
                    disabled={already}
                    title={already ? undefined : [m.desc, m.tip].filter(Boolean).join(' ')}
                    onClick={() => setDraft(m.blank())}
                    className={`w-full h-full text-left border rounded-lg p-3 transition-colors ${
                      already
                        ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
                        : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50/60'}`}
                  >
                    <span className={`flex items-center gap-1.5 text-sm font-medium ${already ? 'text-slate-400' : 'text-slate-800'}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${already ? 'text-slate-300' : 'text-blue-500'}`} />
                      <span className="truncate">{m.label}</span>
                      {already && <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-slate-400">Added</span>}
                    </span>
                    <span className={`block text-xs mt-1 ${already ? 'text-slate-400' : 'text-slate-500'}`}>{m.desc}</span>
                  </button>
                </LockTip>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // ── The rule's own fields ────────────────────────────────────────────────
  const renderParams = () => {
    switch (draft.type) {
      case 'all_active':
        return <p className="text-sm text-slate-600">Everyone currently employed, regardless of department.</p>;

      case 'profile':
        return (
          <>
            <ProfilePicker
              value={draft}
              onChange={(p) => patch(p)}
              depOptions={depOptions}
              desOptions={desOptions}
            />
            <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">
              Where grades come from
              <InfoTip text="The grade behind each designation is set under Dropdown Lists → Designations." />
            </p>
          </>
        );

      case 'individual':
        return (
          <>
            <SearchSelect
              value=""
              onChange={(v) => { if (v) toggle('staffIds', Number(v), true); }}
              options={staffOptions
                .filter((s) => !draft.staffIds.some((id) => String(id) === String(s.id)))
                .map((s) => ({ value: s.id, label: s.name, group: s.position || 'No designation' }))}
              placeholder="Add a person…"
            />
            {draft.staffIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {draft.staffIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-300 rounded text-xs text-slate-700">
                    {staffName(id)}
                    <button type="button" onClick={() => toggle('staffIds', id, false)} className="text-slate-400 hover:text-red-600" title="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </>
        );

      case 'joined_between':
        return <DateRange rule={draft} onPatch={patch} periodHint="This training year. Clear a box for no bound." />;

      case 'qualification':
        return (
          <>
            <div className="flex gap-2 mb-2">
              {[{ v: true, l: 'Holds' }, { v: false, l: 'Does Not Hold' }].map((o) => (
                <button key={o.l} type="button" onClick={() => patch({ held: o.v })} className={pillCls(draft.held === o.v)}>
                  {o.l}
                </button>
              ))}
            </div>
            <CheckColumn
              title="Qualification"
              options={qualTypeOptions.map((q) => ({ value: q.id, label: q.title }))}
              selected={draft.typeIds}
              onChange={(l) => patch({ typeIds: l.map(Number) })}
              empty="No qualifications. Add them under Professional Development → Qualification Types."
              maxH="max-h-48"
            />
          </>
        );

      case 'missed_session':
        return (
          <>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { v: 'no_show', l: 'No-Shows Only', t: 'Did not attend and filed no absence' },
                { v: 'excused', l: 'Excused Absences', t: 'Filed an absence reason' },
                { v: 'any', l: 'Anyone Who Did Not Attend', t: 'Both of the above' },
              ].map((o) => (
                <button key={o.v} type="button" title={o.t} onClick={() => patch({ mode: o.v })} className={pillCls(draft.mode === o.v)}>
                  {o.l}
                </button>
              ))}
            </div>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white">
              {pastSessions.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.sessionIds.some((id) => Number(id) === s.id)}
                    onChange={(e) => toggle('sessionIds', s.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                  />
                  <span className="truncate">{s.courseTitle} — {formatDate(s.date)}</span>
                  <span className="ml-auto shrink-0 flex items-center gap-2">
                    {!s.completed && (
                      <span
                        className="text-[10px] uppercase tracking-wide text-amber-600"
                        title="Not marked complete. Anyone not ticked present counts as having missed it."
                      >
                        Not completed
                      </span>
                    )}
                    <span className="text-xs text-slate-600">{s.attended}/{s.expected} attended</span>
                  </span>
                </label>
              ))}
              {pastSessions.length === 0 && (
                <p className="px-2.5 py-2 text-xs text-slate-500">No earlier sessions to pick from.</p>
              )}
            </div>
          </>
        );

      case 'missing_mandatory':
        return (
          <>
            <select value={draft.courseId} onChange={(e) => patch({ courseId: e.target.value })} className={`${fieldCls} w-full mb-2`}>
              <option value="">Select a category…</option>
              {categoryOptions.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
            <DateRange rule={draft} onPatch={patch} periodHint="This training year. Clear both for all time." />
          </>
        );

      case 'below_target':
        return (
          <>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number" min="0" step="0.5" value={draft.hours}
                onChange={(e) => patch({ hours: e.target.value })}
                placeholder={String(getLdSettings().mandatoryHoursTarget)}
                className={`${fieldCls} w-28`} aria-label="Hours"
              />
              <span className="text-sm text-slate-500">mandatory hours in the period</span>
              <button
                type="button"
                onClick={() => patch({ hours: String(getLdSettings().mandatoryHoursTarget) })}
                className="px-2 py-1 text-xs border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
              >
                Use the Target
              </button>
            </div>
            <DateRange rule={draft} onPatch={patch} periodHint="This training year. Clear both for all time." />
          </>
        );

      default:
        return null;
    }
  };

  const narrowed = meta?.narrowable && !profileIsEmpty(draft.where);

  const title = editing ? 'Edit Rule' : 'Add Expected Attendees';

  return createPortal(
    <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
      <div role="dialog" aria-label={title} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {!draft ? (
            <div className="p-6">{renderTypePicker()}</div>
          ) : (
            // Tall enough for a picker's dropdown (SearchSelect opens up to
            // 15rem below its box) to stay inside the scroll area instead of
            // being clipped when the rule itself is short.
            <div className="grid lg:grid-cols-3 min-h-[26rem]">
              {/* Left: the rule itself */}
              <div className="lg:col-span-2 p-6 space-y-4 lg:border-r border-slate-200">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                      {meta.desc}
                      {meta.tip && <InfoTip text={meta.tip} />}
                    </p>
                  </div>
                  {!editing && (
                    <button
                      type="button"
                      onClick={() => setDraft(null)}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline shrink-0"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Change Type
                    </button>
                  )}
                </div>

                <div>{renderParams()}</div>

                {meta.narrowable && (
                  <div className="border-t border-slate-200 pt-3">
                    <button
                      type="button"
                      onClick={() => setNarrowOpen((v) => !v)}
                      className="text-sm font-medium text-slate-800 hover:text-blue-700"
                    >
                      {narrowOpen ? 'Hide' : 'Narrow Down'} by Department, Grade or Designation
                      {narrowed && !narrowOpen && (
                        <span className="ml-2 text-xs font-normal text-blue-700">
                          {PROFILE_KEYS.reduce((n, k) => n + (draft.where[k]?.length || 0), 0)} ticked
                        </span>
                      )}
                    </button>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Keep only the people in these departments, grades or designations. Nothing ticked means everyone the rule matches.
                    </p>
                    {narrowOpen && (
                      <div className="mt-3">
                        <ProfilePicker
                          value={draft.where}
                          onChange={(w) => patch({ where: w })}
                          depOptions={depOptions}
                          desOptions={desOptions}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: who this rule picks */}
              <div className="p-6 bg-slate-50 lg:rounded-br-lg flex flex-col min-h-[12rem]">
                <p className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                  {matches.loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                  {!complete
                    ? 'Nobody counted yet'
                    : matches.loading && matches.count === null
                      ? 'Working out who…'
                      : matches.error
                        ? 'Preview failed'
                        : `${matches.count ?? 0} ${matches.count === 1 ? 'person' : 'people'} match`}
                </p>
                {!complete && (
                  <p className="text-xs text-slate-500 mt-1">{meta.todo || 'Finish the rule to see who it picks.'}</p>
                )}
                {matches.error && <p className="text-xs text-red-600 mt-1">{matches.error}</p>}
                {complete && !matches.loading && !matches.error && matches.count === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    This rule matches nobody.
                  </p>
                )}
                {trainerStaffIds.length > 0 && complete && (
                  <p className="text-xs text-slate-500 mt-1">
                    {trainerStaffIds.map(staffName).join(', ')}
                    {trainerStaffIds.length === 1 ? ' is a trainer, so they are' : ' are the trainers, so they are'} left out.
                  </p>
                )}
                {matches.data.length > 0 && (
                  <ul className="mt-3 divide-y divide-slate-200 overflow-y-auto max-h-80 lg:max-h-[50vh] border border-slate-200 rounded-lg bg-white">
                    {matches.data.map((p) => (
                      <li key={p.staffId} className="px-2.5 py-1.5 text-sm">
                        <span className="text-slate-800">{p.name}</span>
                        <span className="block text-xs text-slate-500 truncate">
                          {[p.department, p.designation].filter(Boolean).join(' · ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 shrink-0">
          <span className="text-xs text-slate-500">
            {draft && !complete ? meta.todo : ''}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
              Cancel
            </button>
            <button
              type="button"
              disabled={!complete}
              onClick={() => onSave(draft)}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {editing ? 'Save Rule' : 'Add to List'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AudienceRuleModal;
